import React, { useEffect, useRef, useState, useContext } from "react";
import { IoSend } from "react-icons/io5";
import CustomSvg from "../../svgs/CustomSvg";
import { io } from "socket.io-client";
import { UserContext } from "../../../utils/contexts/UserContext";
import { useLocation } from "react-router-dom";



const GAME_BASE_URL = process.env.REACT_APP_GAME_SERVER_URL;

// Message interface for reference
// interface Message {
//   id: string;
//   text: string;
//   sender: string;
//   timestamp: string;
//   pending?: boolean;
// }

export default function ChatMsgs({accountType, activeChat }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isOffline, setIsOffline] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const socketRef = useRef(null);
  const userContext = useContext(UserContext);
  const scrollRef = useRef(null);
  const userId = userContext?.user?._id || "me";
  const [authToken, setAuthToken] = useState(null);
  const [message, setMessage] = useState("");
const location = useLocation();
const selectedUser = location.state?.selectedUser;
const effectiveChat = activeChat || selectedUser;

  useEffect(() => {
    if (effectiveChat?._id) {
      const cached = localStorage.getItem(`chat_messages_${effectiveChat._id}`);
      if (cached) setMessages(JSON.parse(cached));
    }
  }, [effectiveChat]);

  // Auto-scroll to bottom on new message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Setup socket and event handlers
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    const parsedToken = JSON.parse(token);
    // console.log("parsedToken", parsedToken);
    if (!socketRef.current) {
      socketRef.current = io(GAME_BASE_URL, {
        transports: ["websocket"],
        reconnection: true,
        query: {
          userId: userId,
          token: `Bearer ${parsedToken.accessToken}`,
        },
      });
    }

    const socket = socketRef.current;

    // Connection events
    socket.on("connect", () => {
      setIsConnected(true);
      setReconnecting(false);
      // Send any pending messages
      sendPendingMessages();
    });
    socket.on("disconnect", () => {
      setIsConnected(false);
      setReconnecting(true);
    });
    socket.on("reconnect", () => {
      setReconnecting(false);
      sendPendingMessages();
    });
    socket.on("connect_error", (err) => {
      setIsConnected(false);
      setReconnecting(false);
    });

    // Incoming message event
    socket.on("incoming_message", (receivedMessage) => {
      setMessages((prevMessages) => {
        const updatedMessages = [
          ...prevMessages,
          {
            id: receivedMessage._id,
            text: receivedMessage.text,
            sender: receivedMessage.senderId,
            recipient: receivedMessage.recipientId,
            timestamp: receivedMessage.sentAt || receivedMessage.createdAt,
            delivered: receivedMessage.delivered,
            read: receivedMessage.read,
            pending: false,
          },
        ];
        // Cache messages if we have an active chat
        if (effectiveChat?._id) {
          localStorage.setItem(
            `chat_messages_${effectiveChat._id}`,
            JSON.stringify(updatedMessages)
          );
        }
        return updatedMessages;
      });
    });

    // Message sent confirmation
    const handle_message_sent = (data) => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === data.id
            ? {
                ...msg,
                id: data._id,
                sender: data.senderId,
                recipient: data.recipientId,
                timestamp: data.sentAt || data.createdAt,
                delivered: data.delivered,
                read: data.read,
                pending: false,
              }
            : msg
        )
      );
    };
    socket.on("message_sent", handle_message_sent);

    // Message delivered confirmation
    socket.on("message_delivered", (data) => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === data._id ? { ...msg, delivered: true } : msg
        )
      );
    });

    // Message read confirmation
    socket.on("message_read", (data) => {
      setMessages((prev) =>
        prev.map((msg) => (msg.id === data._id ? { ...msg, read: true } : msg))
      );
    });

    // Clean up listeners on unmount
    return () => {
      socket.off("connect");
      socket.off("disconnect");
      socket.off("reconnect");
      socket.off("connect_error");
      socket.off("incoming_message");
      socket.off("message_sent", handle_message_sent);
      socket.off("message_delivered");
      socket.off("message_read");
    };
  }, [effectiveChat?._id]);

  // Send pending messages when reconnected
  const sendPendingMessages = () => {
    const key = `pending_messages_${effectiveChat?._id}`;
    const pendingMessagesJson = localStorage.getItem(key);
    if (!pendingMessagesJson) return;
    const pendingMessages = JSON.parse(pendingMessagesJson);
    if (!socketRef.current?.connected) return;
    pendingMessages.forEach((msg) => {
      socketRef.current.emit("new_message", {
        recipientId: effectiveChat._id,
        text: msg.text,
        timestamp: msg.timestamp || new Date().toISOString(),
        token: sessionStorage.getItem("token"),
      });
    });
    localStorage.removeItem(key);
    setMessages((prev) => prev.map((msg) => ({ ...msg, pending: false })));
  };

  // Send message handler
  const handleSendMessage = async (e) => {
    e.preventDefault();
    const message = input.trim();
    if (!message) return;
    const socket = socketRef.current;
    const messageId = Date.now().toString();
    const timestamp = new Date().toISOString();
    const newMessage = {
      id: messageId,
      text: message,
      sender: userId,
      timestamp: timestamp, // Use ISO string for consistent timestamp format
      pending: !isConnected,
    };
    // Optimistically update UI and cache
    setMessages((prevMessages) => {
      const updated = [...prevMessages, newMessage];
      localStorage.setItem(
        `chat_messages_${effectiveChat._id}`,
        JSON.stringify(updated)
      );
      return updated;
    });
    // Prepare payload
    const token = sessionStorage.getItem("token");
    // const tokens = token ? JSON.parse(token) : {};
    const messagePayload = {
      recipientId: effectiveChat._id,
      text: message,
      timestamp,
      token: `Bearer ${token}`,
      id: messageId,
    };
    try {
      if (isConnected) {
        socket.emit("new_message", messagePayload);
      } else {
        // Store as pending
        const key = `pending_messages_${effectiveChat._id}`;
        const existing = localStorage.getItem(key);
        const pendingMessages = existing ? JSON.parse(existing) : [];
        pendingMessages.push({ ...newMessage, timestamp });
        localStorage.setItem(key, JSON.stringify(pendingMessages));
      }
    } catch (err) {
      // console.error("Error sending/storing message:", err);
    }
    setInput("");
  };

  // Parse and standardize timestamps for sorting
  const parseTimestamp = (timestamp) => {
    if (!timestamp) return new Date(0);

    try {
      if (timestamp instanceof Date) {
        return timestamp;
      }

      const date = new Date(timestamp);
      return isNaN(date.getTime()) ? new Date(0) : date;
    } catch (error) {
      // console.error("Error parsing timestamp:", error);
      return new Date(0);
    }
  };

  // Sort all messages chronologically
  const getSortedMessages = (msgs) => {
    return [...msgs].sort((a, b) => {
      const timeA = parseTimestamp(a.timestamp).getTime();
      const timeB = parseTimestamp(b.timestamp).getTime();
      return timeA - timeB;
    });
  };

  // Format the time for display
  const formatMessageTime = (timestamp) => {
    try {
      const date = parseTimestamp(timestamp);
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch (error) {
      // console.error("Error formatting time:", error);
      return "";
    }
  };

  // Group messages by date (just for date headers)
  const groupByDate = (msgs) => {
    const groups = {};
    const sortedMsgs = getSortedMessages(msgs);

    sortedMsgs.forEach((msg) => {
      try {
        const date = parseTimestamp(msg.timestamp);
        const dateKey = date.toISOString().split("T")[0];

        if (!groups[dateKey]) {
          groups[dateKey] = {
            date: date,
            messages: [],
          };
        }

        groups[dateKey].messages.push(msg);
      } catch (error) {
        // console.error("Error grouping by date:", error);

        // Add to Unknown group
        if (!groups["Unknown"]) {
          groups["Unknown"] = {
            date: null,
            messages: [],
          };
        }
        groups["Unknown"].messages.push(msg);
      }
    });

    return groups;
  };

  // Format date header
  const formatDateHeader = (date) => {
    if (!date) return "Unknown Date";

    try {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      if (date.toDateString() === today.toDateString()) {
        return "Today";
      }

      if (date.toDateString() === yesterday.toDateString()) {
        return "Yesterday";
      }

      return date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      // console.error("Error formatting date header:", error);
      return "Unknown Date";
    }
  };

  // Group messages by date and sort chronologically
  const messagesByDate = groupByDate(messages);

  // Sort date keys chronologically
  const sortedDateKeys = Object.keys(messagesByDate).sort((a, b) => {
    if (a === "Unknown") return -1;
    if (b === "Unknown") return 1;

    return new Date(a) - new Date(b);
  });

  // Render messages in strict chronological order by timestamp
  const displayMsgs = sortedDateKeys.map((dateKey) => {
    const group = messagesByDate[dateKey];
    const dateHeader = formatDateHeader(group.date);

    return (
      <React.Fragment key={dateKey}>
        {/* Date separator */}
        <div className="chat-date-separator">
          <span>{dateHeader}</span>
        </div>

        {/* Messages for this date in strict time order */}
        {group.messages.map((message, i) => {
          const { sender, text, pending, timestamp, delivered, read } = message;
          const isSender = sender === userId;
          const messageTime = formatMessageTime(timestamp);

          return (
            <div key={message.id || i} className="flex flex-col">
              <div
                className={`${
                  isSender
                    ? "self-end bg-pink-600 text-white"
                    : "self-start bg-gray-200 text-black"
                } rounded-lg p-3 my-2 max-w-xs md:max-w-md shadow-md`}
              >
                <div className="whitespace-pre-wrap break-words">{text}</div>

                <div className="flex justify-end items-center mt-1 text-xs text-gray-500">
                  {pending && (
                    <span className="mr-2 text-orange-500">Sending...</span>
                  )}

                  {isSender && (
                    <span className="mr-2 space-y-2">
                      {read && <span className="text-blue-500">✓✓</span>}
                      {delivered && !read && (
                        <span className="text-green-500">✓</span>
                      )}
                      {!delivered && !pending && (
                        <span className="text-gray-400">✓</span>
                      )}
                    </span>
                  )}
                  {messageTime}
                </div>
              </div>
            </div>
          );
        })}
      </React.Fragment>
    );
  });

  return (
    <div className="chat-messages-wrapper">
      <div ref={scrollRef} className="chat-messages-container">
        {messages.length === 0 ? (
          <div className="d-flex flex-column align-items-center justify-content-center h-100 text-center p-4">
            <div style={{ opacity: 0.6, marginBottom: "1rem" }}>
              <CustomSvg name="chat-bubble" />
            </div>

            <p className="text-white opacity-_7 small-txt">
              No messages yet. Start the conversation!
            </p>
          </div>
        ) : (
          displayMsgs
        )}
      </div>

      <div className="chat-input-container">
        <form
          className={`flex justify-between p-2 border ${
              accountType === "celebrity" ? "border-green-600" : " border-pink-300"
            }`}
          onSubmit={handleSendMessage}
        >
          <div className=" mx-2">
            <input
              type="text"
              className="text-white"
              placeholder="Type your message here..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />
          </div>

          <button
            type="submit"
            className="p-2"
            disabled={!input.trim() || !isConnected}
            title={!isConnected ? "Reconnecting..." : "Send message"}
          >
            <IoSend size={18} color="#FFF" />
          </button>
        </form>
      </div>
    </div>
  );
}
